<script setup>
import { ref, onMounted, reactive, computed, watch } from 'vue'
import CloseButton from './CloseButton.vue';

const props = defineProps({
  title: {
    default: "",
  },
  showDone: {
    default: true,
  },
  doneText: {
    default: 'Done',
  },
  doneButtonEnabled: {
    default: true,
  },
  showCancel: {
    default: true,
  },
  cancelText: {
    default: 'Cancel',
  },
  showCloseButton: {
    default: null,
  },
  extraClasses: {
    default: {},
  },
  showFooter: {
    default: true,
  },
  width: {
    default: null,
  },
});

const emit = defineEmits(['onCancel', 'onDone'])

const showX = computed(() => props.showCloseButton !== null ? props.showCloseButton : props.showCancel)

let dialog = ref(null);

function showModal() {
  dialog.value.showModal();
  // Without this, the scroll starts in weird place sometimes
  dialog.value.scrollTop = 0;
}

function closeModal() {
  dialog.value.close();
}

function toggleModal() {
  if (!dialog.value.open) {
    showModal();
  } else {
    closeModal();
  }
}

function onDone() {
  emit('onDone');
  closeModal();
}

function onCancel() {
  emit('onCancel');
  closeModal();
}

function handleNativeCancel() {
  emit('onCancel');
}

defineExpose({
  showModal, closeModal, toggleModal
})

/*
Note to future self: Do not attempt to do a `click away to dismiss` trick here, in javascript.
Ends up being very finnicky.
*/

</script>

<template>
  <Teleport to="body">
    <dialog class="BasicModal" :class="extraClasses" :style="width ? { width } : {}" ref="dialog" @close="" @cancel="handleNativeCancel">
      <!-- Note: Only rendering the body when isOpen messes up the autofocus -->
      <!-- <div v-if="isOpen"> -->
        <div class="InnerModal">
          <div v-if="title" class="TitlePart">
            <h2 class="mb-s">{{ title }}</h2>
          </div>
          <CloseButton v-if="showX" @click="onCancel" class="CloseBtn" />
          <div class="BodyPart">
            <div class="Body" :class="{'mb-m': showFooter}">
              <slot>Default Body</slot>
            </div>
            <div v-if="showFooter" class="Footer Flex" :class="{'centered': extraClasses['centered-footer']}">
              <button class="TertiaryButton" v-if="showCancel" @click="onCancel">{{ cancelText }}</button>
              <button v-if="showDone" @click="onDone" class="CreateBtn DoneBtn" :class="{'WelcomeBtn': extraClasses['welcome-modal']}" :disabled="!doneButtonEnabled">{{ doneText }}</button>
            </div>
          </div>
        </div>
      <!-- </div> -->
    </dialog>
  </Teleport>
</template>

<style scoped>

/* For some reason, the the regular CSS does not seem to work in the modal */
.BasicModal {
  /* min-width: 600px; */
  /* max-width: 90%; */
  width: 500px;
  /* Set max-height to 90% - 16px */
  max-height: calc(90% - 24px);

  border: 1px solid var(--b-40);
  border-radius: var(--br-m);

  /* Note: must be position fixed to avoid weird space at end of body element */
  position: fixed;
  top: 10%;
  left: 50%;
  transform: translate(-50%, 0%);

  overflow-y: auto;
  scrollbar-width: thin;

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  padding: var(--m);
}

dialog {
  /* transition: all 0.1s ease; */
  display: block;
}

dialog[open] {
  opacity: 1;
  /* transform: scale(1.0); */
  /* animation: leaves 2s; */
}

dialog:not([open]) {
  pointer-events: none;
  opacity: 0;
  /* transform: scale(1.1); */
  /* animation: leaves 2s; */
}

/*
@keyframes leaves {
  from {
    transform: scale(1.0);
  }
  to {
    transform: scale(2.0);
  }
}
  */

dialog::backdrop {
  /*backdrop-filter: blur(4px) invert(80%);*/
  /* Note - this invert looks oddly good */
  /*backdrop-filter: invert(80%);*/

  /*backdrop-filter: blur(4px);*/
  background: rgba(0, 0, 0, 0.3);
}

@media (max-width: 600px) {
  .BasicModal {
    position: fixed;
    top: 0;
    left: 0;
    border: none;
    border-radius: 0;
    max-width: 100vw;
    width: 100vw;
    height: 100vh;
    overflow-y: scroll;
  }
}

/*
.TitlePart {
  padding: var(--space-s) var(--space-m);
  border-bottom: 1px solid var(--b-20);
}
*/

/*
.BodyPart {
  padding: var(--space-m) var(--space-m) var(--s) var(--space-m);
}
*/

.Body {
}

.Footer {
  justify-content: flex-end;
}

.Footer.centered {
  justify-content: center;
}

.Footer button {
  margin-right: var(--space-xxs);
}

.Footer button:last-child {
  margin-right: 0;  
}

/*
.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(1.1);
}
 */

 .DoneBtn:disabled {
  background-color: grey;
 }

.InnerModal {
  position: relative;
}

.TitlePart {
  /* Leave room so long titles don't run under the close button */
  padding-right: var(--space-xl);
  margin-bottom: var(--space-s);
}

.TitlePart h2 {
  margin-bottom: 0;
}

.CloseBtn {
  position: absolute;
  top: 0;
  right: 0;
}

</style>
